import { useNavigate } from "react-router-dom";
import { useClient, usePortal, type MemberRow, type RefResolved } from "@app/portal-sdk";
import { Box, Button, PageHeader, SectionCard, Stack, Typography } from "@app/ui-kit";
import { useApiQuery } from "../../lib/useApiQuery";
import { settingsPath, settingsSections } from "./sections";

/**
 * SET-1: the settings index. One card per section a person can reach, with
 * its name, a one-line description and, where it is cheap, a count. Not a
 * redirect to the first section: this page is what teaches someone what is
 * in here.
 */
export const SettingsIndex = () => {
  const { api } = usePortal();
  const { client, canManage } = useClient();
  const navigate = useNavigate();
  const sections = settingsSections({ canManage });
  const reference = useApiQuery<RefResolved>(() => api.request<RefResolved>(`/clients/${client.id}/reference`), [api, client.id]);
  const members = useApiQuery<MemberRow[]>(() => (canManage ? api.request<MemberRow[]>(`/clients/${client.id}/members`) : Promise.resolve([])), [api, client.id, canManage]);

  const count = (key: string): string | null => {
    if (key === "reference") {
      if (reference.error) return null;
      if (!reference.data) return "Counting…";
      const shared = reference.data.domains.filter((d) => d.scope === "client").length;
      const standards = reference.data.domains.filter((d) => d.scope === "platform").length;
      return `${shared} shared list${shared === 1 ? "" : "s"}, ${standards} standard${standards === 1 ? "" : "s"}`;
    }
    if (key === "team") {
      if (members.error) return null;
      if (!members.data) return "Counting…";
      const active = members.data.filter((m) => m.status === "active").length;
      return `${active} ${active === 1 ? "person" : "people"}`;
    }
    if (key === "regional") {
      return `${client.settings.baseCurrency}, ${client.settings.timeZone}, fiscal year ${client.settings.fiscalCalendarConfirmed ? "confirmed" : "unconfirmed"}`;
    }
    return null;
  };

  return (
    <Stack spacing={3}>
      <PageHeader eyebrow={client.name} title="Settings" subtitle="The things that configure this client: what its lists say, how its calendar runs, and who can get in." />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(auto-fill, minmax(280px, 1fr))" }, gap: 2 }}>
        {sections.map((s) => {
          const c = count(s.key);
          return (
            <SectionCard key={s.key} eyebrow={c ?? "Settings"} title={s.label}>
              <Stack spacing={1.5}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>{s.description}</Typography>
                <Box>
                  <Button size="small" variant="outlined" onClick={() => navigate(settingsPath(client.slug, s.key))} aria-label={`Open ${s.label}`}>Open</Button>
                </Box>
              </Stack>
            </SectionCard>
          );
        })}
      </Box>
    </Stack>
  );
};
